import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import {
  Brain,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  FileText,
  Target,
  ArrowRight,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Crosshair,
  TrendingUp,
  TrendingDown,
  Eye,
  Calendar,
  Swords,
  Lightbulb,
  Lock,
  Map,
  LayoutDashboard
} from 'lucide-react';
import axios from 'axios';
import BattlefieldMap from './BattlefieldMap';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const RISK_COLORS = {
  low: 'bg-green-100 text-green-800 border-green-300',
  moderate: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  critical: 'bg-red-100 text-red-800 border-red-300',
};

const URGENCY_COLORS = {
  critical: 'bg-red-600',
  soon: 'bg-orange-500',
  upcoming: 'bg-yellow-500',
  flexible: 'bg-gray-400',
};

const PRIORITY_COLORS = {
  high: 'bg-red-600',
  medium: 'bg-yellow-600',
  low: 'bg-gray-500',
};

const ScoreBar = ({ label, score, inverse = false }) => {
  const effective = inverse ? 100 - score : score;
  const color = effective >= 70 ? '[&>div]:bg-emerald-500' : effective >= 40 ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500';
  const textColor = effective >= 70 ? 'text-emerald-700' : effective >= 40 ? 'text-amber-700' : 'text-red-700';
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <span className={`text-sm font-bold ${textColor}`}>{score}/100</span>
      </div>
      <Progress value={score} className={`h-2 ${color}`} />
    </div>
  );
};

const StrategyBrain = ({ conversationId, isPaid, refreshTrigger, initialView }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    claims: true,
    parties: false,
    timeline: true,
    actions: false,
    pending: true,
    weakLinks: true,
    opposing: true,
    timing: false,
  });
  const [createdAt, setCreatedAt] = useState(null);
  const [viewMode, setViewMode] = useState(initialView || 'overview'); // 'overview' | 'map'

  useEffect(() => { if (initialView) setViewMode(initialView); }, [initialView]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (conversationId && isPaid) fetchExisting();
    else setFetching(false);
  }, [conversationId]);

  // Auto-regenerate when response detection completes or documents are uploaded
  useEffect(() => {
    if (refreshTrigger > 0 && conversationId && isPaid && !loading) {
      setAutoRefreshing(true);
      generateStrategy();
    }
  }, [refreshTrigger]);

  const isStale = (strategyCreatedAt, latestDocUpload, strategyDocCount, currentDocCount) => {
    if (!strategyCreatedAt) return false;
    // New documents uploaded since last generation
    if (currentDocCount > (strategyDocCount || 0)) return true;
    // Latest doc upload is after strategy generation
    if (latestDocUpload && new Date(latestDocUpload) > new Date(strategyCreatedAt)) return true;
    return false;
  };

  const fetchExisting = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/strategy-brain/${conversationId}`, { headers });
      if (res.data.analysis) {
        setAnalysis(res.data.analysis);
        setCreatedAt(res.data.created_at);

        // Check if strategy is stale (new docs uploaded since last generation)
        const stale = isStale(
          res.data.created_at,
          res.data.latest_doc_upload,
          res.data.document_count,
          res.data.current_doc_count
        );
        if (stale && !loading) {
          setAutoRefreshing(true);
          // Small delay so the user sees the existing strategy briefly before refresh
          setTimeout(() => generateStrategy(), 500);
        }
      }
    } catch {
    } finally {
      setFetching(false);
    }
  };

  const generateStrategy = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE}/api/strategy-brain/${conversationId}/generate`, {}, { headers, timeout: 120000 });
      if (res.data.success) {
        setAnalysis(res.data.analysis);
        setCreatedAt(res.data.created_at);
      } else {
        setError('Generation failed. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Strategy generation failed. Please try again.');
    } finally {
      setLoading(false);
      setAutoRefreshing(false);
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const SectionHeader = ({ sectionKey, icon: Icon, title, count, iconColor = 'text-gray-600' }) => (
    <button
      onClick={() => toggleSection(sectionKey)}
      className="flex items-center justify-between w-full py-2"
      data-testid={`section-toggle-${sectionKey}`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <span className="font-semibold text-gray-900">{title}</span>
        {count !== undefined && (
          <Badge variant="secondary" className="text-xs">{count}</Badge>
        )}
      </div>
      {expandedSections[sectionKey]
        ? <ChevronDown className="w-4 h-4 text-gray-400" />
        : <ChevronRight className="w-4 h-4 text-gray-400" />
      }
    </button>
  );

  if (!isPaid) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 max-w-lg" data-testid="strategy-upgrade-gate">
          <CardContent className="p-10 text-center space-y-4">
            <Lock className="w-12 h-12 mx-auto text-amber-600" />
            <h2 className="text-xl font-bold text-gray-900">Paid Feature</h2>
            <p className="text-sm text-gray-700">
              The AI Strategy Brain is available exclusively for paid subscribers. Upgrade to map your case strategically.
            </p>
            <Button
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8"
              onClick={() => { window.location.href = '/dashboard/subscription'; }}
              data-testid="strategy-upgrade-btn"
            >
              Upgrade Plan
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // No analysis yet — show generate prompt
  if (!analysis) {
    return (
      <div className="p-4 sm:p-6 space-y-6" data-testid="strategy-brain-empty">
        <div className="text-center space-y-4 py-8 sm:py-12">
          <Brain className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-blue-600 opacity-80" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">AI Strategy Brain</h2>
          <p className="text-xs sm:text-sm text-gray-600 max-w-lg mx-auto">
            Generate a comprehensive strategic map of your case. The AI will analyze all your documents,
            conversations, and case data to identify claims, parties, timelines, weak points,
            and recommend your next best move.
          </p>
        </div>

        {/* Description Section */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-5 space-y-4 max-w-2xl mx-auto" data-testid="strategy-brain-description">
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-1.5">What is the AI Strategy Brain?</h3>
            <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
              The <strong>AI Strategy Brain</strong> is your case intelligence engine. It takes everything in your case — your client information, opposing party details, the documents you've generated, your mailing records, any responses detected, and uploaded evidence — and produces a <strong>complete strategic analysis</strong> with risk scores, leverage points, weak links, timelines, and recommended next steps. Think of it as having a legal strategist reviewing your entire case file at once.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-1.5">What It Produces</h3>
            <ul className="text-xs sm:text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li><strong>Risk Assessment</strong> — Overall risk level with scores for each area of your case</li>
              <li><strong>Strongest Leverage Points</strong> — Where your case is most powerful</li>
              <li><strong>Weak Links</strong> — Gaps or vulnerabilities with recommendations to address them</li>
              <li><strong>Case Timeline</strong> — Chronological map of every event and deadline</li>
              <li><strong>Predicted Opposing Moves</strong> — What the other side is likely to do and how to prepare</li>
              <li><strong>Pending Action Queue</strong> — Prioritized next steps with deadlines</li>
              <li><strong>Battlefield Map</strong> — Interactive visual graph of all claims, parties, documents, and connections</li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-1.5">How to Use It</h3>
            <ol className="text-xs sm:text-sm text-gray-700 space-y-1 list-decimal list-inside">
              <li><strong>Generate your remedy</strong> on the Workflow tab first — the Strategy Brain needs case data to analyze</li>
              <li><strong>Click "Generate Strategy Map"</strong> below to run the analysis</li>
              <li><strong>Review the Overview</strong> — check your risk scores, leverage points, and weak links</li>
              <li><strong>Switch to the Battlefield Map</strong> — click any node to see details and attach evidence</li>
              <li><strong>Refresh after changes</strong> — the Strategy Brain auto-refreshes when you upload new documents, log mailings, or run Response Detection</li>
            </ol>
          </div>
          <p className="text-[10px] sm:text-xs text-gray-500 italic">
            The Strategy Brain automatically refreshes when new documents are uploaded and incorporates data from the Mailing Room, Response Detection, and Evidence uploads.
          </p>
        </div>

        <div className="text-center">
          <Button
            size="lg"
            className="px-8 bg-blue-700 hover:bg-blue-800"
            onClick={generateStrategy}
            disabled={loading}
            data-testid="generate-strategy-btn"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Analyzing Case...</>
            ) : (
              <><Brain className="w-5 h-5 mr-2" />Generate Strategy Map</>
            )}
          </Button>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </div>
      </div>
    );
  }

  const a = analysis;
  const scores = a.risk_scores || {};

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col overflow-x-hidden w-full max-w-full">
      {/* Header Bar */}
      <div className="p-3 sm:p-4 border-b bg-white flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-blue-700" />
            <h2 className="text-base sm:text-lg font-bold text-gray-900">Strategy Brain</h2>
            {createdAt && (
              <span className="text-[10px] sm:text-xs text-gray-400 hidden sm:inline">
                Generated {new Date(createdAt).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('overview')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-[10px] sm:text-xs font-medium transition-all ${
                  viewMode === 'overview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="view-overview-btn"
              >
                <LayoutDashboard className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                Overview
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-[10px] sm:text-xs font-medium transition-all ${
                  viewMode === 'map' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="view-map-btn"
              >
                <Map className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                Map
              </button>
            </div>
            <Button variant="outline" size="sm" className="h-7 sm:h-8 text-[10px] sm:text-xs px-2 sm:px-3" onClick={generateStrategy} disabled={loading} data-testid="refresh-strategy-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span className="ml-1 hidden sm:inline">{loading ? 'Analyzing...' : 'Refresh'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Auto-refresh banner */}
      {autoRefreshing && loading && (
        <div className="px-3 sm:px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2 flex-shrink-0" data-testid="strategy-auto-refresh-banner">
          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
          <span className="text-xs sm:text-sm text-blue-700 font-medium">Updating strategy with new documents...</span>
        </div>
      )}

      {/* Battlefield Map View */}
      {viewMode === 'map' && (
        <div className="flex-1" data-testid="battlefield-map-container">
          <BattlefieldMap analysis={analysis} conversationId={conversationId} />
        </div>
      )}

      {/* Strategic Overview View */}
      {viewMode === 'overview' && (
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 w-full" style={{overflowWrap: 'anywhere'}} data-testid="strategy-brain-results">

        {/* Strategic Overview Banner */}
        <Card className={`border-2 overflow-hidden ${RISK_COLORS[a.overall_risk_level] || RISK_COLORS.moderate}`}>
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-start gap-2 sm:gap-4">
              <Shield className="w-6 h-6 sm:w-10 sm:h-10 flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-1.5 overflow-hidden">
                <Badge className={`text-[10px] sm:text-sm px-2 py-0.5 ${RISK_COLORS[a.overall_risk_level] || ''}`}>
                  Risk: {(a.overall_risk_level || 'unknown').toUpperCase()}
                </Badge>
                <p className="font-semibold text-xs sm:text-base leading-snug break-words">{a.case_summary}</p>
                <p className="text-[11px] sm:text-sm opacity-80 leading-snug break-words">{a.strategic_position}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Best Next Action */}
        {a.best_next_action && (
          <Card className="border-blue-200 bg-blue-50 overflow-hidden">
            <CardContent className="p-3 sm:p-4 flex items-start gap-2">
              <Target className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1 overflow-hidden">
                <p className="text-[10px] font-semibold text-blue-600 uppercase">Best Next Action</p>
                <p className="text-xs sm:text-sm font-medium text-blue-900 leading-snug break-words">{a.best_next_action}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Risk Scores Grid */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm flex items-center gap-2">
              <Crosshair className="w-4 h-4 text-gray-500" />
              Strategic Risk Scores
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-0 px-3 sm:px-6">
            <ScoreBar label="Claim Clarity" score={scores.claim_clarity || 0} />
            <ScoreBar label="Evidence Strength" score={scores.evidence_strength || 0} />
            <ScoreBar label="Response Completeness" score={scores.response_completeness || 0} />
            <ScoreBar label="Procedural Protection" score={scores.procedural_exposure || 0} />
            <ScoreBar label="Time Sensitivity" score={scores.urgency || 0} />
            <ScoreBar label="Remedy Readiness" score={scores.remedy_readiness || 0} />
          </CardContent>
        </Card>

        {/* Strongest Leverage Points */}
        {a.strongest_leverage_points?.length > 0 && (
          <Card className="border-emerald-200 overflow-hidden">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                Strongest Leverage Points
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0 px-3 sm:px-6">
              {a.strongest_leverage_points.map((point, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-emerald-900 break-words min-w-0">{point}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Weak Links */}
        {a.weakest_links?.length > 0 && (
          <Card className="border-red-200 overflow-hidden">
            <CardHeader className="pb-1 px-3 sm:px-6">
              <SectionHeader sectionKey="weakLinks" icon={TrendingDown} title="Weak Links" count={a.weakest_links.length} iconColor="text-red-600" />
            </CardHeader>
            {expandedSections.weakLinks && (
              <CardContent className="space-y-3 pt-0 px-3 sm:px-6">
                {a.weakest_links.map((wl, idx) => (
                  <div key={idx} className="p-2 sm:p-3 bg-red-50 rounded-lg border border-red-100 space-y-1 overflow-hidden">
                    <div className="flex items-start gap-1.5 flex-wrap">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-xs sm:text-sm font-semibold text-red-900 break-words">{wl.title}</span>
                      <Badge variant="outline" className="text-[9px] sm:text-xs capitalize">{(wl.type || '').replace(/_/g, ' ')}</Badge>
                    </div>
                    <p className="text-[11px] sm:text-xs text-red-800 break-words">{wl.description}</p>
                    <p className="text-[11px] sm:text-xs text-red-700 italic flex items-start gap-1">
                      <Lightbulb className="w-3 h-3 flex-shrink-0 mt-0.5" /> <span className="break-words">{wl.recommendation}</span>
                    </p>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Deadlines */}
        {a.deadlines?.length > 0 && (
          <Card className="border-orange-200 overflow-hidden">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4 text-orange-600" />
                Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0 px-3 sm:px-6">
              {a.deadlines.map((dl, idx) => (
                <div key={idx} className="flex items-start gap-2 sm:gap-3 p-2 rounded-lg bg-orange-50 border border-orange-100">
                  <Badge className={`text-[10px] sm:text-xs ${URGENCY_COLORS[dl.urgency] || 'bg-gray-400'} text-white flex-shrink-0`}>
                    {dl.urgency}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">{dl.description}</p>
                    <p className="text-[10px] sm:text-xs text-gray-500">{dl.date}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Claims */}
        {a.claims?.length > 0 && (
          <Card className="overflow-hidden">
            <CardHeader className="pb-1 px-3 sm:px-6">
              <SectionHeader sectionKey="claims" icon={FileText} title="Claims & Issues" count={a.claims.length} iconColor="text-blue-600" />
            </CardHeader>
            {expandedSections.claims && (
              <CardContent className="space-y-3 pt-0 px-3 sm:px-6">
                {a.claims.map((claim, idx) => (
                  <div key={idx} className="p-2 sm:p-3 bg-gray-50 rounded-lg border space-y-1 overflow-hidden">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{claim.title}</span>
                      <Badge variant="outline" className="text-xs capitalize">{(claim.type || '').replace(/_/g, ' ')}</Badge>
                      <Badge variant="outline" className={`text-xs ${claim.rebuttal_status === 'fully_rebutted' ? 'border-green-300 text-green-700' : claim.rebuttal_status === 'not_rebutted' ? 'border-red-300 text-red-700' : 'border-yellow-300 text-yellow-700'}`}>
                        {(claim.rebuttal_status || '').replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-700 break-words">{claim.description}</p>
                    {claim.supporting_evidence && (
                      <p className="text-xs text-gray-500 break-words">Evidence: {claim.supporting_evidence}</p>
                    )}
                    {claim.source_document && (
                      <p className="text-xs text-blue-600 break-words">Source: {claim.source_document}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Parties */}
        {a.parties?.length > 0 && (
          <Card className="overflow-hidden">
            <CardHeader className="pb-1 px-3 sm:px-6">
              <SectionHeader sectionKey="parties" icon={Users} title="Parties Involved" count={a.parties.length} iconColor="text-indigo-600" />
            </CardHeader>
            {expandedSections.parties && (
              <CardContent className="space-y-3 pt-0 px-3 sm:px-6">
                {a.parties.map((party, idx) => (
                  <div key={idx} className="p-2 sm:p-3 bg-gray-50 rounded-lg border space-y-1 overflow-hidden">
                    <div className="flex items-start gap-1.5 flex-wrap">
                      <span className="text-xs sm:text-sm font-semibold text-gray-900 break-words">{party.name}</span>
                      <Badge variant="outline" className="text-[9px] sm:text-xs capitalize">{(party.role || '').replace(/_/g, ' ')}</Badge>
                      {party.has_responded ? (
                        <Badge className="text-[9px] sm:text-xs bg-green-100 text-green-700">Responded</Badge>
                      ) : (
                        <Badge className="text-[9px] sm:text-xs bg-red-100 text-red-700">No Response</Badge>
                      )}
                    </div>
                    <p className="text-[11px] sm:text-xs text-gray-700 break-words">{party.description}</p>
                    {party.documents_connected?.length > 0 && (
                      <p className="text-[11px] sm:text-xs text-gray-500 break-words">Documents: {party.documents_connected.join(', ')}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Timeline */}
        {a.timeline?.length > 0 && (
          <Card className="overflow-hidden">
            <CardHeader className="pb-1 px-3 sm:px-6">
              <SectionHeader sectionKey="timeline" icon={Clock} title="Case Timeline" count={a.timeline.length} iconColor="text-purple-600" />
            </CardHeader>
            {expandedSections.timeline && (
              <CardContent className="pt-0 px-3 sm:px-6">
                <div className="relative pl-5 sm:pl-6 space-y-3 sm:space-y-4 border-l-2 border-purple-200 ml-1 sm:ml-2">
                  {a.timeline.map((event, idx) => (
                    <div key={idx} className="relative">
                      <div className="absolute -left-[22px] sm:-left-[25px] top-1 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-purple-400 border-2 border-white" />
                      <div className="p-2 rounded-lg bg-gray-50 border overflow-hidden">
                        <div className="flex items-start gap-1.5 flex-wrap">
                          <span className="text-[10px] sm:text-xs font-mono text-purple-700">{event.date}</span>
                          <Badge variant="outline" className="text-[9px] sm:text-xs capitalize">{(event.type || '').replace(/_/g, ' ')}</Badge>
                        </div>
                        <p className="text-xs sm:text-sm font-medium text-gray-900 mt-1 break-words">{event.event}</p>
                        <p className="text-[11px] sm:text-xs text-gray-500 break-words">{event.significance}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Actions Taken */}
        {a.actions_taken?.length > 0 && (
          <Card className="overflow-hidden">
            <CardHeader className="pb-1 px-3 sm:px-6">
              <SectionHeader sectionKey="actions" icon={CheckCircle} title="Actions Taken" count={a.actions_taken.length} iconColor="text-green-600" />
            </CardHeader>
            {expandedSections.actions && (
              <CardContent className="space-y-2 pt-0 px-3 sm:px-6">
                {a.actions_taken.map((act, idx) => (
                  <div key={idx} className="flex items-start gap-2 sm:gap-3 p-2 bg-gray-50 rounded-lg border overflow-hidden">
                    {act.status === 'completed' ? <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> : act.status === 'no_response' ? <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" /> : <Clock className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">{act.action}</p>
                      <p className="text-[11px] sm:text-xs text-gray-500 break-words">{act.date} — {act.next_trigger}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Pending Steps */}
        {a.pending_steps?.length > 0 && (
          <Card className="border-blue-200 overflow-hidden">
            <CardHeader className="pb-1 px-3 sm:px-6">
              <SectionHeader sectionKey="pending" icon={ArrowRight} title="Pending Steps" count={a.pending_steps.length} iconColor="text-blue-600" />
            </CardHeader>
            {expandedSections.pending && (
              <CardContent className="space-y-2 pt-0 px-3 sm:px-6">
                {a.pending_steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 sm:p-3 bg-blue-50 rounded-lg border border-blue-100 overflow-hidden">
                    <Badge className={`text-[10px] sm:text-xs ${PRIORITY_COLORS[step.priority] || 'bg-gray-400'} text-white flex-shrink-0`}>
                      {step.priority}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">{step.step}</p>
                      <p className="text-[11px] sm:text-xs text-gray-600 break-words">{step.reason}</p>
                      {step.deadline && <p className="text-[11px] sm:text-xs text-orange-600 mt-1">Deadline: {step.deadline}</p>}
                    </div>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Predicted Opposing Moves */}
        {a.predicted_opposing_moves?.length > 0 && (
          <Card className="border-slate-300 overflow-hidden">
            <CardHeader className="pb-1 px-3 sm:px-6">
              <SectionHeader sectionKey="opposing" icon={Swords} title="Opposing Moves" count={a.predicted_opposing_moves.length} iconColor="text-slate-600" />
            </CardHeader>
            {expandedSections.opposing && (
              <CardContent className="space-y-3 pt-0 px-3 sm:px-6">
                <p className="text-[10px] sm:text-xs text-gray-400 italic">These are predictions, not guarantees.</p>
                {a.predicted_opposing_moves.map((pm, idx) => (
                  <div key={idx} className="p-2 sm:p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-1 overflow-hidden">
                    <div className="flex items-start gap-1.5 flex-wrap">
                      <Eye className="w-3.5 h-3.5 text-slate-600 flex-shrink-0 mt-0.5" />
                      <span className="text-xs sm:text-sm font-semibold text-gray-900 break-words">{pm.move}</span>
                      <Badge variant="outline" className="text-[9px] sm:text-xs capitalize">{pm.likelihood} likelihood</Badge>
                    </div>
                    <p className="text-[11px] sm:text-xs text-gray-600 break-words">Why: {pm.reason}</p>
                    <p className="text-[11px] sm:text-xs text-blue-700 italic flex items-start gap-1">
                      <Shield className="w-3 h-3 flex-shrink-0 mt-0.5" /> <span className="break-words">Prepare: {pm.preparation}</span>
                    </p>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Suggested Timing */}
        {a.suggested_timing?.length > 0 && (
          <Card className="overflow-hidden">
            <CardHeader className="pb-1 px-3 sm:px-6">
              <SectionHeader sectionKey="timing" icon={Clock} title="Suggested Timing" count={a.suggested_timing.length} iconColor="text-cyan-600" />
            </CardHeader>
            {expandedSections.timing && (
              <CardContent className="space-y-2 pt-0 px-3 sm:px-6">
                {a.suggested_timing.map((t, idx) => (
                  <div key={idx} className="p-2 bg-cyan-50 rounded-lg border border-cyan-100 overflow-hidden">
                    <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">{t.action}</p>
                    <p className="text-[11px] sm:text-xs text-cyan-700">When: {t.when}</p>
                    <p className="text-[11px] sm:text-xs text-gray-600 break-words">{t.reason}</p>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Disclaimer */}
        <div className="text-center text-xs text-gray-400 py-4 border-t space-y-1">
          <p>AI Strategy Brain — Educational analysis only. Not legal advice.</p>
          <p>Consult a qualified professional for complex legal matters.</p>
        </div>
      </div>
    </div>
      )}
    </div>
  );
};

export default StrategyBrain;
